import { FtIn } from './ft-in.js';
import { resolveDimensions, computeBendDeduction, computeTheoreticalLength, aciDefaultMbdMultiplier } from './bend-geometry.js';
import { getBendType } from './bend-catalog.js';
/**
 * Build a BarMark by resolving dimensions, computing theoretical length,
 * and populating pin diameter. Leaves `barMarkId`, `ep`, and `usageCount`
 * empty — those are assigned by `assignBarMarks` in the next pipeline stage.
 *
 * LOSSY: `BarMark.bendDeduction` stores only the FIRST bend's deduction. For
 * multi-bend shapes (e.g., T4 stirrup with 4 bends at mixed angles), the
 * remaining deductions are applied inside `computeTheoreticalLength` and
 * baked into `theoreticalLength`, but the individual values are dropped on
 * the floor. Do NOT sum `bendDeduction` across marks or multiply by bend
 * count expecting total deduction. The schema needs a Phase 3 revision
 * (array shape or `totalBendDeduction`); see follow-up #11 in
 * project_phase2_state.md.
 */
export function buildBarMark(line, bendType, barSize, userDimensions) {
    if (!bendType.validBarSizes.includes(barSize.imperial)) {
        throw new Error(`Line ${line.lineNumber}: bar size #${barSize.imperial} not valid for bend type ${bendType.code} (valid: ${bendType.validBarSizes.join(', ')})`);
    }
    const resolved = resolveDimensions(bendType, userDimensions, barSize);
    const theoreticalLength = computeTheoreticalLength(bendType, resolved, barSize);
    // Use first bend's deduction as representative (for reporting); per-bend deductions
    // are applied inside computeTheoreticalLength already.
    const bendCount = bendType.geometry.filter(s => s.type === 'bend').length;
    const firstDeduction = bendCount > 0 ? computeBendDeduction(bendType, barSize, 0) : FtIn.zero();
    const pinDiameter = computePinDiameter(bendType, barSize);
    return {
        barMarkId: '',
        size: barSize,
        theoreticalLength,
        ep: '',
        bendType: bendType.code,
        dimensions: resolved,
        bendDeduction: firstDeduction,
        pinDiameter,
        usageCount: 0,
    };
}
function computePinDiameter(bendType, barSize) {
    const db = barSize.nominalDiameter;
    if (bendType.pinDiameterRule === 'aci_default') {
        const mult = aciDefaultMbdMultiplier(barSize);
        return FtIn.fromDecimalInches(mult * db);
    }
    return FtIn.fromDecimalInches(bendType.pinDiameterRule.multiplier * db);
}
export function assignBarMarks(job) {
    const strict = job.source === 'pipeline_auto' || job.source === 'pipeline_reviewed';
    const marks = new Map(); // fingerprint -> BarMark
    const fingerprintToId = new Map();
    const manualIds = new Set();
    let nextId = 1;
    // First pass: reserve manual IDs from override lines
    for (const seg of job.segments) {
        for (const ln of seg.lines) {
            if (ln.override && ln.barMark) {
                manualIds.add(ln.barMark);
            }
        }
    }
    const newSegments = job.segments.map((seg) => ({
        ...seg,
        lines: seg.lines.map((line) => {
            if (line.class !== 'L' && line.class !== 'H')
                return line;
            if (!line.size)
                return line;
            const bendTypeCode = extractBendTypeCode(line.notes);
            if (!bendTypeCode) {
                if (strict) {
                    throw new Error(`Line ${seg.segmentId}:${line.lineNumber} (bent) has no bendType in notes`);
                }
                return { ...line, flags: [...line.flags, 'hard'] };
            }
            try {
                const bendType = getBendType(bendTypeCode);
                const userDims = mapLineToUserDims(line, bendType);
                const mark = buildBarMark(line, bendType, line.size, userDims);
                const fingerprint = makeFingerprint(mark);
                let assignedId;
                if (line.override && line.barMark) {
                    assignedId = line.barMark;
                }
                else if (fingerprintToId.has(fingerprint)) {
                    assignedId = fingerprintToId.get(fingerprint);
                }
                else {
                    while (manualIds.has(String(nextId)))
                        nextId++;
                    assignedId = String(nextId++);
                }
                // Register every fingerprint → id mapping (including overrides) so subsequent
                // non-override lines with identical shape inherit the manual id.
                fingerprintToId.set(fingerprint, assignedId);
                const existing = marks.get(fingerprint);
                // usageCount = physical bar count = quantity × multiplier
                // (multiplier > 1 for line-level repeats like spiral turns; default 1 for single bars).
                const addUsage = line.quantity * line.multiplier;
                if (existing) {
                    marks.set(fingerprint, { ...existing, usageCount: existing.usageCount + addUsage });
                }
                else {
                    marks.set(fingerprint, { ...mark, barMarkId: assignedId, usageCount: addUsage });
                }
                return { ...line, barMark: assignedId };
            }
            catch (err) {
                if (strict)
                    throw err;
                return { ...line, flags: [...line.flags, 'hard'] };
            }
        }),
    }));
    return {
        ...job,
        segments: newSegments,
        barMarks: Array.from(marks.values()).sort((a, b) => Number(a.barMarkId) - Number(b.barMarkId)),
    };
}
function extractBendTypeCode(notes) {
    // Catalog codes post-overhaul include Type2, Type8, CUSTOM-HOOK-135, T4, etc.
    // Allow mixed case, digits, and hyphens.
    const m = notes.match(/bendType:([A-Za-z0-9-]+)/);
    return m ? m[1] : null;
}
function mapLineToUserDims(line, bendType) {
    const userDimSpecs = bendType.dimensions.filter((d) => d.source === 'user');
    const slots = [line.L1, line.L2, line.L3, line.L4, line.L5];
    const result = {};
    for (let i = 0; i < userDimSpecs.length && i < slots.length; i++) {
        const val = slots[i];
        if (val)
            result[userDimSpecs[i].label] = val;
    }
    return result;
}
function makeFingerprint(mark) {
    const dimKeys = Object.keys(mark.dimensions).sort();
    const dimStr = dimKeys.map((k) => `${k}=${mark.dimensions[k].toString()}`).join(',');
    return `${mark.bendType}|${mark.size.imperial}|${dimStr}`;
}
